import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";
import "./apiKeys.scss";
import "./companyMembers.scss";
import { getCompanies, getCompanyMembers, addCompanyMember, getCompanyRoles, getModules, updateMemberStatus, getMemberById, updateMember } from '../../Redux/ActionCreators/settingAction';
import { addUser, searchUsers } from '../../Redux/ActionCreators/userAction';
import { clearErrors } from '../../Redux/ActionCreators/errorAction';
import ToggleSwitch from '../../utils/toggleSwitch';
import { useAppPermission } from '../../hooks/useAppPermission';
import { P, canAssignAccountRole } from '../../constants/rbac';
import { isValidEmail } from '../../utils/miscelleneous';

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
    const pagination = [];

    pagination.push(
        <button className="jv2-page-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
    );

    if (currentPage <= 3) {
        pagination.push(
            <button key={1} className={`jv2-page-btn${currentPage === 1 ? ' active' : ''}`} onClick={() => setCurrentPage(1)}>1</button>
        );
    } else {
        pagination.push(<span key="left-dots" className="jv2-page-ellipsis">…</span>);
    }

    const startNum = Math.max(2, currentPage - 1);
    const endNum = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startNum; i <= endNum; i++) {
        pagination.push(
            <button key={i} className={`jv2-page-btn${currentPage === i ? ' active' : ''}`} onClick={() => setCurrentPage(i)}>{i}</button>
        );
    }

    if (currentPage >= totalPages - 2) {
        if (totalPages > 1) {
            pagination.push(
                <button key={totalPages} className={`jv2-page-btn${currentPage === totalPages ? ' active' : ''}`} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
            );
        }
    } else {
        pagination.push(<span key="right-dots" className="jv2-page-ellipsis">…</span>);
    }

    pagination.push(
        <button className="jv2-page-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
    );

    return pagination;
};

// Maps company role code/name to the roleId expected by the addUser API.
// Admin (2) is not assignable through add-user; other roles use the API default (Recruiter).
const USER_ROLE_IDS = {
    RECRUITER: 3,
    MANAGER: 7,
};

const defaultForm = {
    companyId: '',
    fullName: '',
    email: '',
    existingUserId: null,
    role: '',
    isActive: true,
};

const toUserOption = (u) => {
    const email = u.emailId || u.email || '';
    const name = u.name || u.fullName || '';
    const id = u.userID || u.userId || u.id;
    return {
        value: email,
        label: name ? `${name} (${email})` : email,
        userId: id,
        name,
        email,
        __isNew__: false,
    };
};

const ADD_USER_ERRORS = {
    400: 'Could not create the user: the details or role are not valid.',
    403: 'You do not have permission to assign this role.',
    409: 'This email is already registered with another organization.',
};

const EMPTY_EDIT = { member: null, name: '', email: '', companyId: '', roleId: '', errors: {}, saving: false };

const isAdminRole = (role) => String(role?.code || '').toUpperCase() === 'ADMIN';

const findTenantUserIdByEmail = async (email) => {
    try {
        const users = await searchUsers(email, 1, 25);
        const match = users
            .map(toUserOption)
            .find((u) => u.email.trim().toLowerCase() === email.trim().toLowerCase());
        return match?.userId || null;
    } catch {
        return null;
    }
};

const CompanyMembers = () => {
    const dispatch = useDispatch();
    const { can } = useAppPermission();
    const canCreate = can(P.SETTINGS_MEMBERS_CREATE);
    const canEdit = can(P.SETTINGS_MEMBERS_EDIT);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [formErrors, setFormErrors] = useState({});
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [rolesLoading, setRolesLoading] = useState(false);
    const [popupLoading, setPopupLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimerRef = useRef(null);
    const [emailOption, setEmailOption] = useState(null);
    const [edit, setEdit] = useState(EMPTY_EDIT);
    const currentUserId = Number(localStorage.getItem('userId')) || null;

    const SESSION_PAGE_SIZE_KEY = useMemo(() => `companyMembers_pageSize`, []);
    const SESSION_CURRENT_PAGE_KEY = useMemo(() => `companyMembers_currentPage`, []);

    const safeGet = (key, fallback) => {
        try {
            const value = Number(window?.sessionStorage?.getItem(key));
            return Number.isFinite(value) && value > 0 ? value : fallback;
        } catch {
            return fallback;
        }
    };

    const [currentPage, setCurrentPage] = useState(() => safeGet(SESSION_CURRENT_PAGE_KEY, 1));
    const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));

    const companies = useSelector(state => state.setting.companies || []);
    const companyMembers = useSelector(state => state.setting.companyMembers || []);
    const companyMembersTotal = useSelector(state => state.setting.companyMembersTotal || 0);
    const companyRoles = useSelector(state => state.setting.companyRoles || []);

    const activeCompanies = useMemo(
        () => companies.filter((c) => c.is_active !== false),
        [companies]
    );

    const companyOptions = useMemo(
        () => companies.map((company) => ({
            value: company.id,
            label: company.name || company.companyName || company.display_name || company.id,
        })),
        [companies]
    );

    const activeCompanyOptions = useMemo(
        () => activeCompanies.map((company) => ({
            value: company.id,
            label: company.name || company.companyName || company.display_name || company.id,
        })),
        [activeCompanies]
    );

    const selectStyles = {
        control: (base) => ({ ...base, minWidth: 167, borderRadius: "4px", color: "#1f2a37", fontSize: "14px", lineHeight: "18px", fontFamily: '"Inter", sans-serif' }),
        menu: (base) => ({ ...base, minWidth: 167, fontFamily: '"Inter", sans-serif', zIndex: 20 }),
        menuList: (base) => ({ ...base, overflowX: "hidden" }),
        placeholder: (base) => ({ ...base, color: "#1f2a37", fontFamily: '"Inter", sans-serif' }),
        option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    };

    useEffect(() => {
        dispatch(getCompanies(200, 1));
    }, [dispatch]);

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 350);
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, [searchTerm]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize);
    }, [pageSize, SESSION_PAGE_SIZE_KEY]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage);
    }, [currentPage, SESSION_CURRENT_PAGE_KEY]);

    useEffect(() => {
        fetchMembers(selectedCompanyId, pageSize, 1, debouncedSearch);
        setCurrentPage(1);
    }, [selectedCompanyId, pageSize, debouncedSearch]);

    const fetchMembers = async (companyId, pageSize, page = 1, search = '') => {
        setLoading(true);
        await dispatch(getCompanyMembers(companyId, pageSize, page, search));
        setLoading(false);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchMembers(selectedCompanyId, pageSize, page, debouncedSearch);
    };

    const handleToggleMemberStatus = async (member) => {
        const newStatus = !member.is_active;
        const result = await dispatch(updateMemberStatus(member.id, newStatus));
        if (result && result.type === 'UPDATE_MEMBER_STATUS') {
            await dispatch(getMemberById(member.id));
        } else {
            setSnack({ open: true, message: 'Failed to update User status.', severity: 'error' });
        }
    };

    const handleEditClick = async (member) => {
        setEdit({
            ...EMPTY_EDIT,
            member,
            name: member.name || '',
            email: member.email || '',
            companyId: member.company_id || member.company?.id || '',
            roleId: member.roles?.[0]?.id || '',
        });
        if (!companyRoles.length) {
            setRolesLoading(true);
            await dispatch(getCompanyRoles());
            setRolesLoading(false);
        }
    };

    const setEditField = (field, value) =>
        setEdit((prev) => ({ ...prev, [field]: value, errors: { ...prev.errors, [field]: '' } }));

    const handleSaveEdit = async () => {
        const { member } = edit;
        if (!member) return;
        const name = edit.name.trim();
        const email = edit.email.trim().toLowerCase();

        const errors = {};
        if (!name) errors.name = 'Full name is required';
        if (!email) errors.email = 'Email is required';
        else if (!isValidEmail(email)) errors.email = 'Please enter a valid email address';
        if (!edit.companyId) errors.companyId = 'Team is required';
        if (!edit.roleId) errors.roleId = 'Role is required';
        if (Object.keys(errors).length) {
            setEdit((prev) => ({ ...prev, errors }));
            return;
        }

        const payload = {};
        if (name !== (member.name || '')) payload.name = name;
        if (email !== (member.email || '').toLowerCase()) payload.email = email;
        if (edit.companyId !== (member.company_id || member.company?.id || '')) payload.company_id = edit.companyId;
        if (edit.roleId !== (member.roles?.[0]?.id || '')) payload.role_id = edit.roleId;
        if (!Object.keys(payload).length) {
            setEdit(EMPTY_EDIT);
            return;
        }

        setEdit((prev) => ({ ...prev, saving: true }));
        const result = await dispatch(updateMember(member.id, payload));
        if (result?.type !== 'UPDATE_MEMBER') {
            const detail = result?.payload?.detail;
            setSnack({
                open: true,
                message: typeof detail === 'string' ? detail : 'Failed to update the user.',
                severity: 'error',
            });
            setEdit((prev) => ({ ...prev, saving: false }));
            return;
        }

        setEdit(EMPTY_EDIT);
        if (payload.company_id) fetchMembers(selectedCompanyId, pageSize, currentPage, debouncedSearch);
        setSnack({
            open: true,
            message: payload.email || payload.role_id
                ? `User updated. ${name} will see the change after signing in again.`
                : 'User updated.',
            severity: 'success',
        });
    };

    const assignableRoleOptions = useMemo(
        () => companyRoles
            .filter((r) => r.is_active !== false)
            .map((r) => ({ value: r.id, label: r.name || r.role_name || r.title || r.id, role: r })),
        [companyRoles]
    );

    const loadUserOptions = useCallback(async (inputValue) => {
        try {
            const users = await searchUsers(inputValue || '', 1, 25);
            return users.map(toUserOption);
        } catch {
            return [];
        }
    }, []);

    const handleAddClick = async () => {
        setShowModal(true);
        setPopupLoading(true);
        const defaultCompanyId =
            selectedCompanyId && activeCompanies.some((c) => c.id === selectedCompanyId)
                ? selectedCompanyId
                : '';
        setForm({ ...defaultForm, companyId: defaultCompanyId });
        setEmailOption(null);
        setFormErrors({});
        setRolesLoading(true);
        await Promise.all([
            dispatch(getModules()),
            dispatch(getCompanyRoles()),
        ]);
        setRolesLoading(false);
        setPopupLoading(false);
    };

    const validateDetails = () => {
        const errors = {};
        if (!form.companyId) errors.companyId = 'Please select a team';
        if (!form.fullName.trim()) errors.fullName = 'Please enter full name';
        if (!form.email.trim()) errors.email = 'Please enter or select an email';
        else if (!isValidEmail(form.email)) errors.email = 'Please enter a valid email address';
        if (!form.role) errors.role = 'Please assign a role';
        return errors;
    };

    const handleSave = async () => {
        setSaving(true);
        const errors = validateDetails();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setSaving(false);
            return;
        }

        let resolvedUserId = form.existingUserId;

        try {
            if (!resolvedUserId) {
                const selectedRole = companyRoles.find(r => r.id === form.role);
                const roleKey = (selectedRole?.code || selectedRole?.name || '').toUpperCase();
                const accountRoleId = canAssignAccountRole(JSON.parse(localStorage.getItem('profileDetails')))
                    ? USER_ROLE_IDS[roleKey]
                    : undefined;
                const newUserResult = await dispatch(addUser({
                    name: form.fullName.trim(),
                    emailId: form.email.trim(),
                    ...(accountRoleId ? { roleId: accountRoleId } : {}),
                }));

                if (newUserResult?.type === 'ADD_USERS') {
                    resolvedUserId = newUserResult.payload?.userId
                        || newUserResult.payload?.userID
                        || newUserResult.payload?.data?.userId
                        || newUserResult.payload?.id;
                } else {
                    const status = newUserResult?.payload?.status;
                    // 409 is also returned when the account already exists in this tenant with the same role
                    if (status === 409) {
                        resolvedUserId = await findTenantUserIdByEmail(form.email);
                        if (resolvedUserId) dispatch(clearErrors());
                    }
                    if (!resolvedUserId) {
                        setSnack({
                            open: true,
                            message: ADD_USER_ERRORS[status] || 'Could not create the user. Please try again.',
                            severity: 'error',
                        });
                        setSaving(false);
                        return;
                    }
                }

                if (!resolvedUserId) {
                    setSnack({ open: true, message: 'Could not retrieve new user.', severity: 'error' });
                    setSaving(false);
                    return;
                }
            }

            const payload = {
                user_id: resolvedUserId,
                role_id: form.role,
                is_primary: false,
                is_active: true,
                name: form.fullName.trim(),
                email: form.email.trim(),
            };

            const effectiveCompanyId = form.companyId || selectedCompanyId;
            const result = await dispatch(addCompanyMember(effectiveCompanyId, payload));
            if (result && result.type === 'ADD_COMPANY_MEMBER') {
                setShowModal(false);
                setSnack({ open: true, message: 'User added successfully!', severity: 'success' });
                fetchMembers(selectedCompanyId, pageSize, 1, debouncedSearch);
            } else {
                const apiMsg =
                    result?.payload?.detail
                    || result?.error
                    || 'Failed to add User.';
                setSnack({ open: true, message: typeof apiMsg === 'string' ? apiMsg : 'Failed to add User.', severity: 'error' });
            }
        } catch (error) {
            setSnack({ open: true, message: 'Something went wrong.', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const totalPages = Math.ceil(companyMembersTotal / pageSize) || 1;
    const pagedMembers = companyMembers;

    return (
        <div className="job-table-container settings-fill-page">

            <div className="top-navigation">
                <div className='createjob-tab'>
                    <h4>Team Users</h4>
                    {canCreate && (
                        <button className="create-job" onClick={handleAddClick} style={{ width: 'unset' }}>
                            Add Users
                        </button>
                    )}
                </div>
            </div>

            <div className="table-content" style={{ background: '#FFFFFF', borderRadius: '8px' }}>
                <div className="settings-filter-bar" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div className="has-search" style={{ flex: 1, maxWidth: 320, minWidth: 200 }}>
                        <span className="form-control-feedback">
                            <img src="/images/search.svg" alt="" />
                        </span>
                        <input
                            type="text"
                            className={`search-bar${searchTerm ? ' has-clear' : ''}`}
                            placeholder="Search users by name or email"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="search-clear"
                                title="Clear search"
                                aria-label="Clear search"
                                onClick={() => setSearchTerm('')}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <CreatableSelect
                        inputId="company-select"
                        value={
                            selectedCompanyId
                                ? companyOptions.find((c) => c.value === selectedCompanyId) || null
                                : null
                        }
                        onChange={(option) => setSelectedCompanyId(option ? option.value : "")}
                        options={companyOptions}
                        isClearable
                        isSearchable
                        placeholder="Select a Team"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />
                </div>
                <div className="jv2-table-outer">
                    <div className="jv2-table-scroll">
                        <table className="jv2-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Team</th>
                                    <th>Active</th>
                                    <th>Role</th>
                                    {canEdit && <th style={{ textAlign: 'center' }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={canEdit ? 6 : 5} className="jv2-loading-cell">
                                            <div className="spinner-border" role="status" style={{ color: '#F75891', width: '28px', height: '28px' }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : companyMembers.length === 0 ? (
                                    <tr><td colSpan={canEdit ? 6 : 5} className="jv2-empty-cell">No Users found</td></tr>
                                ) : (
                                    pagedMembers.map(member => (
                                        <tr key={member.id}>
                                            <td>{member.name || '—'}</td>
                                            <td>{member.email || '—'}</td>
                                            <td>{member.company?.name || '—'}</td>
                                            <td>
                                                {canEdit ? (
                                                    <ToggleSwitch
                                                        checked={!!member.is_active}
                                                        onChange={() => handleToggleMemberStatus(member)}
                                                    />
                                                ) : (
                                                    <span>{member.is_active ? 'Active' : 'Inactive'}</span>
                                                )}
                                            </td>
                                            <td>{member.roles?.[0]?.name || '—'}</td>
                                            {canEdit && (
                                                <td style={{ textAlign: 'center' }}>
                                                    {Number(member.user_id) !== currentUserId && (
                                                        <img
                                                            src="/images/edit-black-icon.svg"
                                                            alt="Edit user"
                                                            title="Edit user"
                                                            width={16}
                                                            height={16}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleEditClick(member)}
                                                        />
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {!loading && (
                        <div className="jv2-pagination">
                            <span className="jv2-pagination-info">
                                {companyMembersTotal ? (
                                    <>Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, companyMembersTotal)} of <strong>{companyMembersTotal}</strong> Users</>
                                ) : 'Showing 0 of 0'}
                            </span>
                            <div className="jv2-pagination-right">
                                <div className="jv2-page-btns">
                                    {renderCustomPagination(totalPages, currentPage, handlePageChange)}
                                </div>
                                <div className="jv2-per-page">
                                    <span>Per page:</span>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ADD MEMBER MODAL */}
            {showModal && (
                <div className="upload-modal automation-modal">
                    <div className="modal-content member-modal-content">
                        <div className="modal-head">
                            <h3>Add User</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={() => setShowModal(false)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>

                        {popupLoading ?
                            <div
                                className="loading-overlay"
                                style={{ display: 'flex', justifyContent: 'center', alignItems: "center", height: "400px" }}
                            >
                                <div
                                    className="spinner-border"
                                    role="status"
                                    style={{ width: "1rem", height: "1rem" }}
                                >
                                    <span className="visually-hidden">
                                        Loading...
                                    </span>
                                </div>
                            </div>
                            :
                            <>
                                <div className="modal-input">
                                    <div className="automation-dropdown">
                                        <label>Select Team *</label>
                                        <CreatableSelect
                                            inputId="add-company-select"
                                            value={
                                                form.companyId
                                                    ? activeCompanyOptions.find((c) => c.value === form.companyId) || null
                                                    : null
                                            }
                                            onChange={(option) => {
                                                const companyId = option ? option.value : "";
                                                setForm({ ...form, companyId });
                                                setFormErrors({ ...formErrors, companyId: '' });
                                            }}
                                            options={activeCompanyOptions}
                                            isClearable
                                            isSearchable
                                            placeholder="Select a team"
                                            maxMenuHeight={200}
                                            classNamePrefix="rs-filter"
                                            styles={selectStyles}
                                        />
                                        {formErrors.companyId && (
                                            <span className="field-error">{formErrors.companyId}</span>
                                        )}
                                    </div>

                                    <div className="automation-dropdown">
                                        <label>Email Address *</label>
                                        <AsyncCreatableSelect
                                            inputId="add-user-email-select"
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadUserOptions}
                                            value={emailOption}
                                            onChange={(option) => {
                                                if (!option) {
                                                    setEmailOption(null);
                                                    setForm({ ...form, email: '', fullName: '', existingUserId: null });
                                                    setFormErrors({ ...formErrors, email: '', fullName: '' });
                                                    return;
                                                }
                                                if (option.__isNew__) {
                                                    setEmailOption(option);
                                                    setForm({
                                                        ...form,
                                                        email: option.value,
                                                        existingUserId: null,
                                                    });
                                                } else {
                                                    setEmailOption(option);
                                                    setForm({
                                                        ...form,
                                                        email: option.email || option.value,
                                                        fullName: option.name || form.fullName,
                                                        existingUserId: option.userId || null,
                                                    });
                                                }
                                                setFormErrors({ ...formErrors, email: '', fullName: '' });
                                            }}
                                            onCreateOption={(inputValue) => {
                                                const option = {
                                                    value: inputValue.trim(),
                                                    label: inputValue.trim(),
                                                    __isNew__: true,
                                                };
                                                setEmailOption(option);
                                                setForm({
                                                    ...form,
                                                    email: inputValue.trim(),
                                                    existingUserId: null,
                                                });
                                                setFormErrors({ ...formErrors, email: '' });
                                            }}
                                            isValidNewOption={(input) => isValidEmail(input)}
                                            formatCreateLabel={(input) => `Create new user "${input.trim()}"`}
                                            noOptionsMessage={({ inputValue }) =>
                                                inputValue
                                                    ? 'No matching user. Type a full email address (e.g. name@company.com) to add a new user.'
                                                    : 'Type to search users'
                                            }
                                            placeholder="Search existing user or type new email"
                                            maxMenuHeight={200}
                                            classNamePrefix="rs-filter"
                                            styles={selectStyles}
                                            isClearable
                                        />
                                        {formErrors.email && (
                                            <span className="field-error">{formErrors.email}</span>
                                        )}
                                    </div>

                                    <div className="automation-dropdown">
                                        <label>Full Name *</label>
                                        <input
                                            type="text"
                                            placeholder="Enter full name"
                                            value={form.fullName}
                                            onChange={e => {
                                                setForm({ ...form, fullName: e.target.value });
                                                setFormErrors({ ...formErrors, fullName: '' });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: formErrors.fullName ? '1px solid red' : '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none',
                                            }}
                                        />
                                        {formErrors.fullName && (
                                            <span className="field-error">{formErrors.fullName}</span>
                                        )}
                                    </div>

                                    <div className="automation-dropdown">
                                        <label>Assign Role *</label>
                                        <CreatableSelect
                                            inputId="add-role-select"
                                            value={
                                                form.role
                                                    ? companyRoles
                                                        .map((r) => ({
                                                            value: r.id,
                                                            label: r.name || r.role_name || r.title || r.id,
                                                        }))
                                                        .find((r) => r.value === form.role) || null
                                                    : null
                                            }
                                            onChange={(option) => {
                                                setForm({ ...form, role: option ? option.value : "" });
                                                setFormErrors({ ...formErrors, role: '' });
                                            }}
                                            options={companyRoles.map((r) => ({
                                                value: r.id,
                                                label: r.name || r.role_name || r.title || r.id,
                                            }))}
                                            isClearable
                                            isSearchable
                                            isLoading={rolesLoading}
                                            placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'}
                                            maxMenuHeight={200}
                                            classNamePrefix="rs-filter"
                                            styles={selectStyles}
                                        />
                                        {formErrors.role && (
                                            <span className="field-error">{formErrors.role}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end" style={{ margin: '0 30px 20px 30px' }}>
                                    <button
                                        className="upload-btn"
                                        onClick={() => setShowModal(false)}
                                        style={{ backgroundColor: 'transparent', border: '1px solid #111928', color: '#111928', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="upload-btn"
                                        onClick={handleSave}
                                        disabled={saving}
                                        style={{
                                            opacity: saving ? 0.5 : 1,
                                            cursor: saving ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {saving ? 'Add User...' : 'Add User'}

                                    </button>
                                </div>
                            </>
                        }
                    </div>
                </div>
            )}

            {/* EDIT USER MODAL */}
            {edit.member && (() => {
                const currentRole = edit.member.roles?.[0];
                const selected = assignableRoleOptions.find((o) => o.value === edit.roleId) || null;
                const promotingToAdmin = selected && isAdminRole(selected.role) && !isAdminRole(currentRole);
                const removingAdmin = selected && isAdminRole(currentRole) && !isAdminRole(selected.role);
                const currentTeamId = edit.member.company_id || edit.member.company?.id;
                const teamOptions = activeCompanyOptions.some((o) => o.value === currentTeamId) || !currentTeamId
                    ? activeCompanyOptions
                    : [{ value: currentTeamId, label: edit.member.company?.name || 'Current team' }, ...activeCompanyOptions];
                const inputStyle = (hasError) => ({
                    width: '100%',
                    padding: '8px 12px',
                    border: hasError ? '1px solid red' : '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                });
                return (
                    <div className="upload-modal automation-modal">
                        <div className="modal-content member-modal-content" style={{ maxHeight: 'unset' }}>
                            <div className="modal-head">
                                <h3>Edit User</h3>
                                <img
                                    src="/images/close-icon.svg"
                                    alt="close"
                                    onClick={() => !edit.saving && setEdit(EMPTY_EDIT)}
                                    style={{ cursor: 'pointer' }}
                                />
                            </div>

                            <div className="modal-input">
                                <div className="automation-dropdown">
                                    <label>Full Name *</label>
                                    <input
                                        type="text"
                                        placeholder="Enter full name"
                                        value={edit.name}
                                        maxLength={255}
                                        disabled={edit.saving}
                                        onChange={(e) => setEditField('name', e.target.value)}
                                        style={inputStyle(edit.errors.name)}
                                    />
                                    {edit.errors.name && <span className="field-error">{edit.errors.name}</span>}
                                </div>
                                <div className="automation-dropdown">
                                    <label>Email Address *</label>
                                    <input
                                        type="email"
                                        placeholder="name@company.com"
                                        value={edit.email}
                                        disabled={edit.saving}
                                        onChange={(e) => setEditField('email', e.target.value)}
                                        style={inputStyle(edit.errors.email)}
                                    />
                                    {edit.errors.email && <span className="field-error">{edit.errors.email}</span>}
                                </div>
                                <div className="automation-dropdown">
                                    <label>Team *</label>
                                    <Select
                                        inputId="edit-team-select"
                                        value={teamOptions.find((o) => o.value === edit.companyId) || null}
                                        onChange={(option) => setEditField('companyId', option ? option.value : '')}
                                        options={teamOptions}
                                        isDisabled={edit.saving}
                                        placeholder="Select a team"
                                        maxMenuHeight={200}
                                        classNamePrefix="rs-filter"
                                        styles={selectStyles}
                                    />
                                    {edit.errors.companyId && <span className="field-error">{edit.errors.companyId}</span>}
                                </div>
                                <div className="automation-dropdown">
                                    <label>Role *</label>
                                    <Select
                                        inputId="edit-role-select"
                                        value={selected}
                                        onChange={(option) => setEditField('roleId', option ? option.value : '')}
                                        options={assignableRoleOptions}
                                        isLoading={rolesLoading}
                                        isDisabled={edit.saving}
                                        placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'}
                                        maxMenuHeight={200}
                                        classNamePrefix="rs-filter"
                                        styles={selectStyles}
                                    />
                                    {edit.errors.roleId && <span className="field-error">{edit.errors.roleId}</span>}
                                    {promotingToAdmin && (
                                        <span className="field-error" style={{ color: '#b45309' }}>
                                            Admins get full access, including Settings, billing and managing users.
                                        </span>
                                    )}
                                    {removingAdmin && (
                                        <span className="field-error" style={{ color: '#b45309' }}>
                                            This user will lose Admin access, including Settings.
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end" style={{ margin: '0 30px 20px 30px' }}>
                                <button
                                    className="upload-btn"
                                    onClick={() => setEdit(EMPTY_EDIT)}
                                    style={{ backgroundColor: 'transparent', border: '1px solid #111928', color: '#111928', cursor: edit.saving ? 'not-allowed' : 'pointer', opacity: edit.saving ? 0.5 : 1 }}
                                    disabled={edit.saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="upload-btn"
                                    onClick={handleSaveEdit}
                                    disabled={edit.saving}
                                    style={{
                                        opacity: edit.saving ? 0.5 : 1,
                                        cursor: edit.saving ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {edit.saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default CompanyMembers;
